<script setup>
import { ref, toRef } from 'vue'
import { Dialog } from 'quasar'
import { getI18n } from '@/plugins/i18n'
import { useDetectDevice } from '@/composables/device'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useGridRef, useODataSource, useDefaultGridOptions, useLookup, refreshGridAsync, getCreatedRowId } from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useChips } from '@/composables/chips'
import { useNotify } from '@/composables/notify'
import { useStaffMovements } from '@/composables/staff-movements'
import { scheduleApi } from '@/apis/schedule.api'
import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useScheduleStore } from '@/stores/schedule.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import VacationEditor from '@/components/shared/VacationEditor.vue'
import StaffMovementsEdit from '@/components/shared/StaffMovementsEdit.vue'
import NoStockConfirmDialog from '@/components/shared/NoStockConfirmDialog.vue'

const props = defineProps({
  service: { type: Object, default: null },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const scheduleStore = useScheduleStore()
const webSiteStore = useWebSiteStore()
const { gridRef } = useGridRef()
const { getVacationStatusChips, getEventTypeChips } = useChips()
const { notifySuccess } = useNotify()
const { noStockDialog, approveAllocationAsync, revertAllocationAsync } = useStaffMovements()

const editVacation = ref(null)
const addAllocation = ref(null)
const onlyAbsences = toRef(webSiteStore, 'staffMovementsOnlyAbsences')

const isAbsence = (data) => data?.type === scheduleStore.eventTypeEnum.ABSENCE

// The grid rows are already collapsed by the backend (one row per assignment / per absence),
// so the group's key fields come straight off the row.
const rowGroup = (row) => ({ serviceId: row.service_id, projectId: row.project_id, budgetDocumentRowId: row.budget_document_row_id })

const gridDataSource = useODataSource(scheduleApi.getAbsoluteUrl() + scheduleApi.eventsUrl, {
  customParamsFn: () => ({
    service_type: portalStore.serviceTypeEnum.STAFF,
    group_by_allocation: true,
    ...(props.service ? { staff_ids: props.service.id } : {}),
  }),
})

const refreshAsync = () => gridRef.value.instance.refresh()

const onOnlyAbsencesChange = (value) =>
  gridRef.value?.instance?.columnOption('type', 'filterValue', value ? scheduleStore.eventTypeEnum.ABSENCE : null)

// The persisted grid state clears every column filterValue on load (see useDefaultStateStoring),
// so re-apply the toggle's type filter once the grid is ready.
let absencesFilterSynced = false
const onContentReady = () => {
  if (absencesFilterSynced) return
  absencesFilterSynced = true
  if (onlyAbsences.value) onOnlyAbsencesChange(true)
}

// An assignment (budget row) is approved/reverted as a whole; an absence is a single event.
const onApprove = async (e) => {
  const row = e.row.data
  if (row.budget_document_row_id) {
    await approveAllocationAsync(rowGroup(row), refreshAsync)
    return
  }
  await scheduleStore.upsertEventAsync({ id: row.id, confirmed: true })
  notifySuccess(i18n.t('info.updatedSuccessfully'))
  await refreshAsync()
}

const onRevert = async (e) => {
  const row = e.row.data
  if (row.budget_document_row_id) {
    await revertAllocationAsync(rowGroup(row), refreshAsync)
    return
  }
  await scheduleStore.upsertEventAsync({ id: row.id, confirmed: false })
  notifySuccess(i18n.t('info.updatedSuccessfully'))
  await refreshAsync()
}

const onDelete = (e) => {
  Dialog.create({ message: i18n.t('common.confirmDelete'), cancel: true, persistent: true }).onOk(async () => {
    await scheduleStore.deleteEventAsync(e.row.data.id)
    notifySuccess(i18n.t('info.deletedSuccessfully'))
    await gridRef.value.instance.refresh()
  })
}

const columns = [
  {
    type: 'buttons',
    buttons: [
      {
        icon: 'edit',
        hint: i18n.t('common.edit'),
        visible: (e) => isAbsence(e.row?.data),
        onClick: (e) => { editVacation.value = { ...e.row.data } },
      },
      {
        icon: 'trash',
        hint: i18n.t('common.delete'),
        visible: (e) => isAbsence(e.row?.data),
        onClick: onDelete,
      },
      {
        icon: 'check',
        hint: i18n.t('common.approve'),
        visible: (e) => !e.row?.data?.confirmed && webSiteStore.can(webSiteStore.permissionFlagEnum.APPROVALS),
        onClick: onApprove,
      },
      {
        icon: 'mdi mdi-undo-variant',
        hint: i18n.t('common.restore'),
        visible: (e) => e.row?.data?.confirmed && webSiteStore.can(webSiteStore.permissionFlagEnum.APPROVALS),
        onClick: onRevert,
      },
    ],
    fixed: false,
    visibleIndex: -1,
    showInColumnChooser: false,
    width: '8rem',
  },
  dataGridColumn.useAvatarColumn({
    cellTemplate: (element, { data }) => {
      const svc = planningStore.getServices().find(s => s.id === data.service_id)
      const src = svc?.avatar_file || svc?.avatar_url
      if (!src) return
      const img = document.createElement('img')
      img.src = src
      img.style.cssText = 'width:2rem;height:2rem;object-fit:cover;border-radius:50%;display:block;margin:auto'
      element.appendChild(img)
    },
  }),
  {
    dataField: 'service_id',
    caption: i18n.t('common.service'),
    width: '14rem',
    visible: !props.service,
    lookup: useLookup({
      dataSource: planningStore.getServices(),
      valueExpr: 'id',
      displayExpr: (item) => planningStore.getServiceName(item),
    }),
  },
  {
    dataField: 'type',
    caption: i18n.t('common.type'),
    width: '10rem',
    filterValue: onlyAbsences.value ? scheduleStore.eventTypeEnum.ABSENCE : undefined,
    lookup: useLookup({
      dataSource: scheduleStore.getLocalizedEventTypes(),
      valueExpr: 'id',
      displayExpr: 'name',
    }),
    cellTemplate: 'type-cell-template',
  },
  {
    dataField: 'confirmed',
    caption: i18n.t('common.status'),
    width: '10rem',
    cellTemplate: 'status-cell-template',
  },
  {
    dataField: 'start',
    caption: i18n.t('common.startDate'),
    dataType: 'datetime',
    width: '12rem',
    sortOrder: 'desc',
  },
  {
    dataField: 'end',
    caption: i18n.t('common.endDate'),
    dataType: 'datetime',
    width: '12rem',
  },
  dataGridColumn.useProjectColumn({ dataField: 'project_id', width: '14rem' }),
]

const gridOptions = useDefaultGridOptions({
  id: props.service ? 'staff-movements-detail-grid-v2' : 'staff-movements-grid-v4',
  columns,
  dataSource: gridDataSource,
  isDetailGrid: !!props.service,
  addButton: {
    onClick: () => { addAllocation.value = { service: props.service?.id ?? null } },
  },
  viewToolbarItems: [{ location: 'after', template: 'only-absences-toggle' }],
  onContentReady,
})

const updateGrid = async (saved) => {
  const createdId = getCreatedRowId(addAllocation.value, saved)
  editVacation.value = null
  addAllocation.value = null
  await refreshGridAsync(gridRef, createdId)
}
</script>

<template>
  <div>
    <no-stock-confirm-dialog v-model="noStockDialog" />
    <q-dialog
      :maximized="isMobile"
      :model-value="editVacation !== null"
      class="dialog-md"
      @hide="updateGrid"
    >
      <vacation-editor
        v-if="editVacation !== null"
        :model-value="editVacation"
        @update="updateGrid"
        @cancel="updateGrid"
      />
    </q-dialog>
    <q-dialog :model-value="addAllocation !== null" class="dialog-md" @hide="updateGrid">
      <staff-movements-edit
        v-if="addAllocation !== null"
        :service="addAllocation.service ?? null"
        @update="updateGrid"
        @cancel="updateGrid"
      />
    </q-dialog>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #only-absences-toggle>
        <q-toggle
          v-model="onlyAbsences"
          :label="i18n.t('common.showAbsencesOnly')"
          color="primary"
          style="align-self: flex-end;"
          @update:model-value="onOnlyAbsencesChange"
        />
      </template>
      <template #type-cell-template="{ data: templateOptions }">
        <chip-template :values="getEventTypeChips(templateOptions.data.type)" />
      </template>
      <template #status-cell-template="{ data: templateOptions }">
        <chip-template :values="getVacationStatusChips(templateOptions.data.confirmed)" />
      </template>
    </dx-data-grid>
  </div>
</template>
